import { useEffect, useRef, useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import api from "@/lib/api";

// Avatar tint cycles through a small palette derived from the brand so each
// homeowner's initial circle stays visually distinct without feeling random.
const TINTS = [
  { bg: "#FFE8DE", fg: "#C75D33" },
  { bg: "#DCFCE7", fg: "#15803D" },
  { bg: "#DBEAFE", fg: "#1D4ED8" },
  { bg: "#FEF3C7", fg: "#92400E" },
  { bg: "#F3E8FF", fg: "#6D28D9" },
];

const STORAGE_KEY = "wsq_social_proof_dismissed";

// Timings (ms) — first show after a small delay so visitors get oriented; then
// each toast displays for `VISIBLE_MS`, then hides for `HIDDEN_MS` before the
// next one in the rotation.
const INITIAL_DELAY_MS = 4500;
const VISIBLE_MS = 7000;
const HIDDEN_MS = 9000;

export default function SocialProofToaster() {
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const tickRef = useRef(null);

  // Fetch the anonymized recent-quote feed once on mount.
  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    api
      .get("/public/social-proof")
      .then(({ data }) => { if (!cancelled) setItems(data?.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dismissed]);

  // Drive the show/hide rotation.
  useEffect(() => {
    if (dismissed || items.length === 0) return undefined;

    let idx = 0;
    const showNext = () => {
      setActiveIdx(idx);
      setVisible(true);
      tickRef.current = setTimeout(() => {
        setVisible(false);
        tickRef.current = setTimeout(() => {
          idx = (idx + 1) % items.length;
          showNext();
        }, HIDDEN_MS);
      }, VISIBLE_MS);
    };

    const start = setTimeout(showNext, INITIAL_DELAY_MS);
    return () => {
      clearTimeout(start);
      if (tickRef.current) clearTimeout(tickRef.current);
    };
  }, [items, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  if (dismissed || items.length === 0 || activeIdx < 0) return null;

  const item = items[activeIdx];
  if (!item) return null;
  const tint = TINTS[activeIdx % TINTS.length];
  const initial = (item.name || "?").trim().charAt(0).toUpperCase() || "?";
  const locationText = item.location ? `in ${item.location}` : "in their area";

  return (
    <div
      data-testid="social-proof-toaster"
      aria-live="polite"
      style={{
        position: "fixed",
        left: 16,
        bottom: 16,
        zIndex: 50,
        maxWidth: 320,
        pointerEvents: visible ? "auto" : "none",
        transform: visible ? "translateY(0)" : "translateY(140%)",
        opacity: visible ? 1 : 0,
        transition: "transform 420ms cubic-bezier(.2,.9,.25,1), opacity 320ms ease",
      }}
    >
      <div
        className="flex items-start gap-3 bg-white border border-slate-200 rounded-2xl shadow-xl px-3.5 py-3 pr-9"
        style={{ boxShadow: "0 10px 30px -10px rgba(15,23,42,0.18)" }}
      >
        <div
          className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm"
          style={{ background: tint.bg, color: tint.fg }}
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-slate-900 font-semibold leading-snug">
            <span data-testid="social-proof-name">{item.name}</span>
            <span className="font-normal text-slate-600"> got {item.service} {locationText}</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            <span data-testid="social-proof-age">{item.age}</span>
            <span aria-hidden="true">·</span>
            <span>Verified quote</span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          data-testid="social-proof-dismiss"
          className="absolute top-2 right-2 w-6 h-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
